// =============================================================================
// O Painel do Gestor dentro do Arena — "Orçamento da área" (22/10)
//
// Pedido do Enzo, com o OK do João e do Mateus: o gestor usa o painel de
// dentro do portal do colaborador, sem abrir o Nexa. O Arena mostra
// `/embed/gestor` numa moldura, e aqui entram só as quatro abas do gestor, as
// mesmas do Nexa. Financeiro, Solicitações e Acessos não existem neste modo.
//
// O login continua sendo o do Nexa, e nunca o do Arena: confiar na identidade
// que o Arena manda daria a quem controla o Arena (no Lovable, onde todo o
// time alcança tudo) a entrada no Nexa como qualquer gestor — o que a
// hospedagem separada existe para impedir (CLAUDE.md §4.3). Como o Google
// recusa aparecer dentro de outra página, o login abre numa janelinha; a
// sessão vale 90 dias renovados no uso, e a tela percebe a entrada sozinha.
// Os dois estão no domínio da empresa: para o navegador são o mesmo site, e o
// cookie da sessão vale dentro da moldura.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FaixaAbas, periodoInicial } from "./App.tsx";
import { EVENTO_SEM_SESSAO } from "./lib/embutido.ts";
import { PainelGestor } from "./paginas/PainelGestor.tsx";
import { OrcadoRealizado } from "./paginas/OrcadoRealizado.tsx";
import { FerramentasArea } from "./paginas/FerramentasArea.tsx";
import { PropostaGestor } from "./paginas/PropostaGestor.tsx";

type Aba = "gerencial" | "analitica" | "ferramentas" | "proposta";

const ABAS: { id: Aba; rotulo: string; meta: string }[] = [
  { id: "gerencial", rotulo: "Gerencial", meta: "Gráficos" },
  { id: "analitica", rotulo: "Analítica", meta: "Orçado vs Realizado" },
  { id: "ferramentas", rotulo: "Ferramentas", meta: "Da área e pedidos" },
  { id: "proposta", rotulo: `Orçamento ${new Date().getFullYear() + 1}`, meta: "Proposta da área" },
];

type Eu = { autenticado: true; usuario: { nome: string; email: string } | null; papeis: string[]; perfil_dev: boolean };

/** Quem está logado; nulo sem sessão. Sem o `api.ts`: o 401 aqui é resposta, não erro. */
async function lerEu(): Promise<Eu | null> {
  const r = await fetch("/auth/eu", { credentials: "same-origin" });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`auth/eu ${r.status}`);
  return (await r.json()) as Eu;
}

export function Embutido() {
  const qc = useQueryClient();
  // `undefined`: ainda conferindo; `null`: sem sessão.
  const [eu, setEu] = useState<Eu | null | undefined>(undefined);
  const [semConexao, setSemConexao] = useState(false);
  const [aba, setAba] = useState<Aba>("gerencial");
  const [periodo, setPeriodo] = useState<string[]>(periodoInicial);
  const anterior = useRef<Eu | null | undefined>(undefined);

  const conferir = useCallback(async (): Promise<Eu | null> => {
    try {
      const e = await lerEu();
      setEu(e);
      setSemConexao(false);
      return e;
    } catch {
      setSemConexao(true);
      return null;
    }
  }, []);

  useEffect(() => { void conferir(); }, [conferir]);

  // Sessão que venceu no meio do uso: a API avisa (api.ts), e a tela volta ao login.
  useEffect(() => {
    const sem = () => setEu(null);
    window.addEventListener(EVENTO_SEM_SESSAO, sem);
    return () => window.removeEventListener(EVENTO_SEM_SESSAO, sem);
  }, []);

  // Acabou de entrar: o que falhou por falta de sessão é buscado de novo.
  useEffect(() => {
    if (anterior.current === null && eu) void qc.resetQueries();
    anterior.current = eu;
  }, [eu, qc]);

  if (eu === undefined) {
    return (
      <Centro>
        {semConexao ? "Não foi possível falar com o Nexa Finance. Verifique a conexão e recarregue a página." : "Carregando…"}
      </Centro>
    );
  }
  if (eu === null) return <Entrar conferir={conferir} />;

  return (
    <div className="min-h-screen pb-10">
      {eu.perfil_dev && (
        <p role="status" className="border-b border-atencao-linha bg-atencao-bg px-4 py-2 text-[12.5px] text-atencao-tinta">
          <strong>Perfil Dev · staging.</strong> Tudo nesta tela vem do banco de staging, com dados sintéticos.
        </p>
      )}
      <div className="pagina pt-4">
        <FaixaAbas abas={ABAS} pagina={aba} onIr={setAba} />
      </div>
      <main>
        {aba === "gerencial" && <PainelGestor periodo={periodo} onMudarPeriodo={setPeriodo} />}
        {aba === "analitica" && <OrcadoRealizado periodo={periodo} onMudarPeriodo={setPeriodo} escopo="gestor" />}
        {aba === "ferramentas" && <FerramentasArea />}
        {aba === "proposta" && <PropostaGestor />}
      </main>
    </div>
  );
}

function Centro({ children }: { children: React.ReactNode }) {
  return <div className="grid min-h-screen place-items-center p-6 text-center text-[14px] text-tinta-media">{children}</div>;
}

/**
 * O login pela janelinha. Enquanto ela está aberta, a tela pergunta a cada 2
 * segundos se a sessão já existe (por 5 minutos): não depende de a janela
 * conseguir avisar quem a abriu, o que a página do Google pode cortar.
 */
function Entrar({ conferir }: { conferir: () => Promise<Eu | null> }) {
  const [esperando, setEsperando] = useState(false);
  const [bloqueada, setBloqueada] = useState(false);

  useEffect(() => {
    if (!esperando) return;
    let vezes = 0;
    const t = setInterval(() => {
      vezes++;
      void conferir().then((e) => {
        if (!e && vezes >= 150) { clearInterval(t); setEsperando(false); }
      });
    }, 2000);
    return () => clearInterval(t);
  }, [esperando, conferir]);

  const abrir = () => {
    const janela = window.open("/auth/login?janela=1", "nexa-login", "popup=yes,width=520,height=680");
    if (!janela) { setBloqueada(true); return; }
    setBloqueada(false);
    setEsperando(true);
  };

  return (
    <Centro>
      <div className="w-full max-w-md rounded-xl border border-linha bg-superficie p-6 text-left shadow-sm">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Nexa Finance</p>
        <h1 className="mt-1 font-display text-[19px] font-bold tracking-tight text-tinta">Orçamento da área</h1>
        <p className="mt-2 text-[13.5px] text-tinta-media">
          Para ver o orçamento e as ferramentas da sua área, entre com a sua conta Google da empresa. Depois disso, você
          não precisa entrar de novo enquanto usar o painel pelo menos uma vez a cada 90 dias.
        </p>
        <button type="button" onClick={abrir}
          className="mt-4 w-full rounded-lg bg-laranja px-4 py-2.5 text-[13.5px] font-bold text-azul">
          {esperando ? "Esperando o login na janela…" : "Entrar com a conta Google"}
        </button>
        {esperando && (
          <p className="mt-2 text-[12px] text-tinta-fraca">
            Termine o login na janela que abriu. O painel aparece aqui sozinho.
          </p>
        )}
        {bloqueada && (
          <p className="mt-2 text-[12px] text-atencao-tinta">
            O navegador não deixou abrir a janela de login. Libere as janelas deste site, ou{" "}
            <a href="/" target="_blank" rel="noopener" className="underline">abra o Nexa Finance no navegador</a>.
          </p>
        )}
        <p className="mt-4 border-t border-linha pt-3 text-[12px] text-tinta-fraca">
          No aplicativo de celular, se a janela não abrir,{" "}
          <a href="/" target="_blank" rel="noopener" className="underline">abra o Nexa Finance no navegador</a>.
        </p>
      </div>
    </Centro>
  );
}
